<script setup>
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  configuration_modules: { type: Array,  default: () => [] },
  labels:                { type: Object, default: () => ({}) },
  save_url:              { type: String, default: '' },
  back_url:              { type: String, default: '/administration' },
})

const { error: toastError } = useToast()

// true si v ∈ { true, 'true', 't', 1, '1' }
// false si v ∈ { false, 'false', 'f', 0, '0' }
// null sinon (texte libre)
function boolish(v) {
  if (v === true  || v === 'true'  || v === 't' || v === 1 || v === '1') return true
  if (v === false || v === 'false' || v === 'f' || v === 0 || v === '0') return false
  return null
}

// Indique si un module est booléen par nature (basé sur valeur d'origine)
function isBooleanModule(cm) {
  return boolish(cm.valeur) !== null
}

// Shape attendu par le controller : params[:configuration_modules][id][valeur]
// Booléen stocké comme bool JS, texte comme string. Transformé à l'envoi.
const initialPayload = {}
props.configuration_modules.forEach(m => {
  const b = boolish(m.valeur)
  initialPayload[String(m.id)] = {
    valeur: b === null ? (m.valeur ?? '') : b,
  }
})

const form = useForm({
  configuration_modules: initialPayload,
})

function submit() {
  // Convertir les booléens en "1"/"0" pour rester compatible avec le format DB existant
  const payload = {}
  Object.entries(form.configuration_modules).forEach(([id, row]) => {
    const v = row.valeur
    payload[id] = { valeur: typeof v === 'boolean' ? (v ? '1' : '0') : v }
  })

  form.transform(() => ({ configuration_modules: payload })).post(props.save_url, {
    preserveScroll: true,
    onError: (errs) => {
      const list = Object.values(errs).flat()
      if (list.length) list.forEach(m => toastError(m))
      else toastError('Erreur lors de la mise à jour.')
    },
  })
}

function cancel() {
  window.location.href = props.back_url
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Configuration &rsaquo; Modules
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Configuration des modules
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ configuration_modules.length }} paramètre{{ configuration_modules.length > 1 ? 's' : '' }} de module
            </p>
          </div>
          <a
            :href="back_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Sous-navigation -->
        <nav class="flex items-center gap-1 bg-surface-container-low rounded-md p-1 border border-outline-variant/10 mb-6">
          <a href="/administration" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Liste</a>
          <a href="/administration/edit_config_client" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Client</a>
          <a href="/administration/edit_logos" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Logos</a>
          <a href="/administration/edit_config_bordereau" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Bordereau</a>
          <a href="/administration/edit_config_importation" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Importation</a>
          <a href="/administration/edit_config_module" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-primary bg-surface-container rounded-sm border-l-4 border-tertiary">Modules</a>
        </nav>

        <!-- Form Card -->
        <form @submit.prevent="submit" class="space-y-4">
          <section class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-outline-variant/10">
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-1/3">{{ labels.module || 'Module' }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-1/3">{{ labels.attribut || 'Attribut' }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.valeur || 'Valeur' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="configuration_modules.length === 0">
                    <td colspan="3" class="px-6 py-6 text-center text-sm text-outline/60">
                      Aucune configuration de module.
                    </td>
                  </tr>
                  <tr
                    v-for="cm in configuration_modules"
                    :key="cm.id"
                    class="border-b border-outline-variant/5 hover:bg-surface-container-high/20 transition-colors"
                  >
                    <!-- Readonly : Module -->
                    <td class="px-4 py-3 font-bold text-on-surface">
                      {{ cm.module || '—' }}
                    </td>
                    <!-- Readonly : Attribut -->
                    <td class="px-4 py-3 font-mono text-outline">
                      {{ cm.attribut || '—' }}
                    </td>
                    <!-- Éditable : Valeur (toggle si booléen, sinon text) -->
                    <td class="px-4 py-2">
                      <!-- Toggle pour valeurs booléennes (0/1/true/false) -->
                      <label v-if="isBooleanModule(cm)" class="relative inline-flex items-center cursor-pointer">
                        <input
                          v-model="form.configuration_modules[String(cm.id)].valeur"
                          type="checkbox"
                          class="sr-only peer"
                        />
                        <div class="w-10 h-5 bg-surface-container-highest rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-on-surface after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-5 peer-checked:after:bg-on-primary"></div>
                      </label>
                      <!-- Input texte libre pour valeurs non booléennes -->
                      <input
                        v-else
                        v-model="form.configuration_modules[String(cm.id)].valeur"
                        type="text"
                        class="w-full bg-surface-container-highest border-none rounded-sm text-on-surface placeholder:text-outline/50 px-2 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              @click="cancel"
              class="px-6 py-3 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="form.processing"
              class="px-8 py-3 machined-sheen text-on-primary font-black rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
            >
              <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
              <span v-else class="material-symbols-outlined text-base">save</span>
              Enregistrer
            </button>
          </div>
        </form>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
